import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { calcGridPositionsForVerticalMonth, getLocalDate } from 'lib/datetime/calendarUtils'
import { last } from 'lib/array/arrayUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import CalendarSidebarDayCell from './CalendarSidebarDayCell'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'

const Grid = styled.div`
  display: grid;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-bottom: 0;
  position: relative;
  grid-template-columns: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(7 , 1fr);
    border: none;
  }
`

const Root = styled.div`
  width: 100%;
`

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  display: grid;
  height: 100%;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const SelectedRange = styled.div``

interface Props {
  onDatesChange: (day: App.BedbankCalendarDay) => void;
  checkIn?: string;
  checkOut?: string;
  days: Array<App.BedbankCalendarDay>;
  className?: string;
  duration: number;
  fetching: boolean;
  flights: boolean;
  enableCalendarRecommendations: boolean;
  adultsNumber: number;
  roomsNumber: number;
  displayPricingAsPerPerson: boolean;
}

function CalendarSidebarDaysMonth(props: Props) {
  const {
    className,
    days,
    checkIn,
    checkOut,
    duration,
    fetching,
    flights,
    enableCalendarRecommendations,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
    onDatesChange,
  } = props

  const firstDate = days[0]

  const selectedRange = useMemo(() => {
    if (checkIn && checkOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(checkIn),
        getLocalDate(checkOut),
      ).range
    }
    return undefined
  }, [checkIn, checkOut, firstDate])

  const onDayClick = useCallback((day: App.BedbankCalendarDay) => {
    onDatesChange(day)
  }, [onDatesChange])

  return (
    <Root className={className}>
      <Grid>
        {days.map((day, index) => <CalendarSidebarDayCell
          key={day.checkIn}
          duration={duration}
          price={day.price}
          propertyFees={day.propertyFees}
          day={day.day}
          hasPromo={day.hasPromo}
          checkIn={day.checkIn}
          fetching={fetching}
          flights={flights}
          flightPrice={day.flightPrice}
          isCheckIn={day.checkIn === checkIn}
          isCheckOut={day.checkOut === checkOut}
          isBetweenDay={!!(selectedRange && (index + 1) >= selectedRange.start && index < (selectedRange.end - 1))}
          onClick={() => onDayClick(day)}
          enableCalendarRecommendations={enableCalendarRecommendations}
          displayPricingAsPerPerson={displayPricingAsPerPerson}
          adultsNumber={adultsNumber}
          roomsNumber={roomsNumber}
        />)}
        <GridOverlay style={{ gridTemplateRows: `repeat(${last(days).day - firstDate.day + 1}, 1fr)` }}>
          {(checkIn && checkOut) && selectedRange && <SelectedRange style={{
            gridRowStart: selectedRange.start,
            gridRowEnd: selectedRange.end,
          }}
          />}
        </GridOverlay>
      </Grid>
    </Root>
  )
}

export default withOfferPageState((state) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  enableCalendarRecommendations: shouldEnableCalendarRecommendations(state.bedbankOffer),
}), CalendarSidebarDaysMonth)
